import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { FirstLoginRunbook } from "@/components/FirstLoginRunbook";

function ReadinessList({ items }) {
  return (
    <ul className="space-y-1" data-testid="sharekhan-readiness-list">
      {items.map((i) => (
        <li key={i.key} data-testid={`readiness-${i.key}`} className={`break-words ${i.ok ? "text-emerald-400" : i.required ? "text-amber-300" : "text-slate-400"}`}>
          {i.ok ? "✓" : "•"} {i.detail}
        </li>
      ))}
    </ul>
  );
}

function TestResult({ r }) {
  return (
    <div data-testid="sharekhan-test-result" className={`rounded-md border p-3 space-y-1 ${r.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
      <div className={r.ok ? "text-emerald-300" : "text-amber-300"}>{r.ok ? "Read-only test PASSED" : "Read-only test did not pass"} — {r.detail}</div>
      {r.operations.map((o) => (
        <div key={o.operation} data-testid={`sharekhan-test-${o.operation}`} className="text-slate-400">
          {o.ok ? "✓" : "•"} {o.operation}: {o.ok ? `${o.items_returned} item(s) returned (values not shown)` : o.detail}
        </div>
      ))}
      <div className="text-slate-400" data-testid="sharekhan-test-closed">Session disconnected and login window closed. Orders possible: no.</div>
    </div>
  );
}

function LinkControls({ status, onResult }) {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const refresh = () => ["sharekhan-status", "sharekhan-readiness"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const connect = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/auth/broker/step-up", { totp_code: code });
      return (await api.get("/brokers/sharekhan/connect", { params: { step_up_token: data.step_up_token } })).data;
    },
    onSuccess: (r) => { setCode(""); window.location.assign(r.login_url); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const test = useMutation({
    mutationFn: async () => (await api.post("/brokers/sharekhan/auth-test")).data,
    onSuccess: (r) => { onResult(r); refresh(); },
    onError: (e) => { toast.error(formatApiError(e.response?.data?.detail)); refresh(); },
  });
  const disconnect = useMutation({
    mutationFn: async () => (await api.post("/brokers/sharekhan/disconnect")).data,
    onSuccess: () => { refresh(); toast.success("Sharekhan session removed and login window closed"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const win = status?.login_window;
  return (
    <div className="space-y-2">
      <div data-testid="sharekhan-login-window" className="text-slate-400">
        Temporary login window: {win?.open ? <span className="text-amber-300">OPEN until {win.expires_at?.slice(11, 16)} UTC</span> : "closed"}
      </div>
      {status?.connected ? (
        <div className="flex flex-wrap gap-2 items-center">
          <span data-testid="sharekhan-connected" className="text-emerald-400">Linked (read-only)</span>
          <Button size="sm" data-testid="sharekhan-auth-test-btn" onClick={() => test.mutate()} disabled={test.isPending} className="bg-blue-600 hover:bg-blue-500 text-white">Run read-only test (funds + holdings, then disconnect)</Button>
          <Button size="sm" variant="outline" data-testid="sharekhan-disconnect-btn" onClick={() => disconnect.mutate()} className="border-[var(--border-subtle)]">Disconnect now</Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input data-testid="sharekhan-stepup-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit authenticator code" className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] max-w-[220px]" />
          <Button data-testid="sharekhan-connect-btn" onClick={() => connect.mutate()} disabled={code.length !== 6 || connect.isPending} className="bg-blue-600 hover:bg-blue-500 text-white shrink-0">Sign in to Sharekhan (read-only)</Button>
        </div>
      )}
    </div>
  );
}

function LastFailure() {
  const { data } = useQuery({ queryKey: ["sharekhan-last-failure"], queryFn: async () => (await api.get("/brokers/sharekhan/last-failure")).data });
  const f = data?.failure;
  if (!f) return null;
  return (
    <div data-testid="sharekhan-last-failure" className="text-amber-300">
      Last sign-in failure: {f.reason}{f.http_status ? ` (HTTP ${f.http_status})` : ""} at {String(f.at).slice(0, 16).replace("T", " ")} UTC
      {f.diag?.origin && <span data-testid="sharekhan-last-failure-origin"> · origin: {f.diag.origin}{f.diag.message ? ` — ${f.diag.message}` : ""}</span>}
      {f.session_encoding && <span data-testid="sharekhan-last-failure-encoding"> · session: {f.session_encoding}</span>}
    </div>
  );
}

export function SharekhanLinkPanel() {
  const [result, setResult] = useState(null);
  const ready = useQuery({ queryKey: ["sharekhan-readiness"], queryFn: async () => (await api.get("/brokers/sharekhan/readiness")).data });
  const live = !!ready.data?.live_enabled;
  const status = useQuery({ queryKey: ["sharekhan-status"], enabled: live, queryFn: async () => (await api.get("/brokers/sharekhan/status")).data });
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connected") === "1") { toast.success("Sharekhan linked (read-only). Now run the read-only test.", { id: "sk-link" }); }
    if (p.get("link") === "failed") { toast.error(`Sharekhan sign-in did not complete. Nothing was saved.${p.get("reason") ? ` Reason: ${p.get("reason").replace(/[^a-z-]/g, "")}${p.get("http") ? ` (HTTP ${p.get("http").replace(/\D/g, "")})` : ""}` : ""}`, { id: "sk-link", duration: 15000 }); }
    if (p.has("connected") || p.has("link")) { window.history.replaceState(null, "", window.location.pathname); }
  }, []);
  return (
    <Panel title="Sharekhan · first controlled read-only sign-in" testId="sharekhan-link-panel" actions={<KeyRound className="w-4 h-4 text-slate-500" />}>
      <div className="p-4 space-y-3 text-xs">
        <p className="text-slate-400">Reads funds and holdings once, then disconnects and closes the login window. Orders cannot be placed, modified or cancelled: the connection has no order routes, whatever any switch says.</p>
        {ready.data && <ReadinessList items={ready.data.items} />}
        <LastFailure />
        {ready.data && (
          <div data-testid="sharekhan-ready-summary" className={ready.data.ready_for_first_login ? "text-emerald-400" : "text-amber-300"}>
            {ready.data.ready_for_first_login ? "All required items are ready for the first login." : "Not ready yet: complete the amber items above."}
          </div>
        )}
        {live ? (status.isLoading ? <p className="text-slate-400">Checking link…</p> : <LinkControls status={status.data} onResult={setResult} />) : (
          <p data-testid="sharekhan-link-locked" className="text-slate-400">Sign-in is locked until you approve the first read-only test (live switch OFF).</p>
        )}
        {result && <TestResult r={result} />}
        <FirstLoginRunbook />
      </div>
    </Panel>
  );
}
